'use client';

import AttachmentGallery, {
  type AttachmentView
} from '@components/files/AttachmentGallery';
import GenerateImagePanel, {
  type GenerateImageKindHint
} from '@components/files/GenerateImagePanel';
import { Button } from '@components/ui/button';
import Field from '@components/ui/field';
import {
  FILE_ACCEPT,
  MAX_FILES_PER_PARENT,
  type FileParentType
} from '@files/constants';
import {
  prepareLocalFiles,
  revokePreview,
  uploadPendingFile,
  type PendingLocalFile,
  type UploadTargetContext
} from '@files/upload-client';
import { useZero } from '@rocicorp/zero/react';
import { CloudUpload, RotateCcw } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { DragEvent, FC, useEffect, useRef, useState } from 'react';
import { runZeroMutation } from '../../zero/run-mutation';
import { useCurrentOrgUser } from '../../zero/use-current-user';

type ParentAttachmentsProps = {
  organizationId: string;
  parentId: string;
  parentType: Extract<FileParentType, 'menus' | 'recipes' | 'ingredients'>;
  files: AttachmentView[];
  canEdit: boolean;
  onBeforeUpload?: () => Promise<boolean>;
  generation?: { kindHint?: GenerateImageKindHint };
};

const ParentAttachments: FC<ParentAttachmentsProps> = ({
  organizationId,
  parentId,
  parentType,
  files,
  canEdit,
  onBeforeUpload,
  generation
}) => {
  const t = useTranslations('files');
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const z = useZero() as any;
  const orgUser = useCurrentOrgUser(organizationId);
  const inputRef = useRef<HTMLInputElement>(null);
  const uploadingRef = useRef(false);
  const pendingCleanupRef = useRef<PendingLocalFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [pending, setPending] = useState<PendingLocalFile[]>([]);
  const [progress, setProgress] = useState<Record<string, number>>({});
  const [error, setError] = useState<string | null>(null);
  pendingCleanupRef.current = pending;

  useEffect(() => () => pendingCleanupRef.current.forEach(revokePreview), []);

  const target: UploadTargetContext = {
    organizationId,
    parentId,
    parentType
  };

  const persistPending = async (queue: PendingLocalFile[]) => {
    if (!canEdit || uploadingRef.current || !queue.length) {
      return;
    }
    uploadingRef.current = true;
    let remaining = [...queue];
    try {
      if (onBeforeUpload && !(await onBeforeUpload())) {
        return;
      }
      setUploading(true);
      setError(null);
      for (const candidate of queue) {
        const uploaded = await uploadPendingFile(candidate, target, value =>
          setProgress(current => ({ ...current, [candidate.id]: value }))
        );
        const retained = {
          ...candidate,
          ...uploaded,
          localFile: undefined
        };
        remaining = remaining.map(file =>
          file.id === candidate.id ? retained : file
        );
        setPending(remaining);
        const saved = await runZeroMutation(
          z.mutate.files.create({
            ...uploaded,
            organizationId,
            parentId,
            parentType,
            uploadedByUserId: orgUser?.userId,
            now: Date.now()
          }),
          { errorTitle: t('upload-error') }
        );
        if (!saved) {
          throw new Error('persist-failed');
        }
        revokePreview(candidate);
        remaining = remaining.filter(file => file.id !== candidate.id);
        setPending(remaining);
      }
    } catch {
      setPending(remaining);
      setError(t('upload-error'));
    } finally {
      uploadingRef.current = false;
      setProgress({});
      setUploading(false);
    }
  };

  const addFiles = async (list: File[]) => {
    if (!canEdit || uploadingRef.current) {
      return;
    }
    const result = await prepareLocalFiles(
      list,
      MAX_FILES_PER_PARENT - files.length - pending.length
    );
    if (result.errors.length) {
      setError(
        result.errors
          .map(code => t(`validation-${code}` as Parameters<typeof t>[0]))
          .join(' ')
      );
    }
    if (!result.files.length) {
      return;
    }
    const queue = [...pending, ...result.files];
    setPending(queue);
    await persistPending(queue);
  };

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (canEdit) {
      void addFiles(Array.from(event.dataTransfer.files ?? []));
    }
  };

  const pendingViews: AttachmentView[] = pending.map(file => ({
    id: file.id,
    url: '',
    previewUrl: file.previewUrl,
    filename: file.filename,
    contentType: file.contentType,
    sizeBytes: file.sizeBytes,
    width: file.width,
    height: file.height
  }));
  const currentProgress = Math.round(
    Math.max(0, ...Object.values(progress), 0)
  );

  return (
    <Field
      label={t('title')}
      htmlFor="parent-attachments"
      description={t('help')}
    >
      <div
        onDragOver={event => {
          if (canEdit) {
            event.preventDefault();
          }
        }}
        onDrop={onDrop}
        className="flex flex-col gap-3"
      >
        <AttachmentGallery
          files={files}
          variant="grid"
          canDelete={() => canEdit}
          onDelete={file =>
            runZeroMutation(
              z.mutate.files.remove({ id: file.id, organizationId }),
              { errorTitle: t('remove-error') }
            )
          }
        />
        {generation ? (
          <GenerateImagePanel
            organizationId={organizationId}
            targetType={parentType}
            targetId={parentId}
            canEdit={canEdit}
            kindHint={generation.kindHint}
          />
        ) : null}
        <AttachmentGallery
          files={pendingViews}
          variant="strip"
          onRemove={id => {
            setPending(current => {
              const removed = current.find(file => file.id === id);
              if (removed) {
                revokePreview(removed);
              }
              return current.filter(file => file.id !== id);
            });
            setError(null);
          }}
        />
        {canEdit ? (
          <>
            <input
              id="parent-attachments"
              ref={inputRef}
              type="file"
              className="sr-only"
              accept={Object.keys(FILE_ACCEPT).join(',')}
              multiple
              disabled={
                uploading ||
                files.length + pending.length >= MAX_FILES_PER_PARENT
              }
              onChange={event => {
                const picked = event.target.files;
                if (picked?.length) {
                  void addFiles(Array.from(picked));
                }
                event.target.value = '';
              }}
            />
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                variant="outline"
                disabled={
                  uploading ||
                  files.length + pending.length >= MAX_FILES_PER_PARENT
                }
                onClick={() => inputRef.current?.click()}
              >
                <CloudUpload className="size-4" />
                {uploading ? t('uploading') : t('upload')}
              </Button>
              {error && pending.length ? (
                <Button
                  type="button"
                  variant="ghost"
                  disabled={uploading}
                  onClick={() => void persistPending(pending)}
                >
                  <RotateCcw className="size-4" />
                  {t('retry')}
                </Button>
              ) : null}
            </div>
          </>
        ) : null}
        {uploading ? (
          <p className="text-meta text-muted-foreground" role="status">
            {t('upload-progress', { percent: currentProgress })}
          </p>
        ) : null}
        {error ? (
          <p className="text-meta text-destructive" role="alert">
            {error}
          </p>
        ) : null}
      </div>
    </Field>
  );
};

export default ParentAttachments;
